import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import dayjs from 'dayjs'
import { IconButton, Tooltip } from '@mui/material'
import { useContext, useEffect, useMemo, useState } from 'react'
import { Spinner } from 'react-bootstrap'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import closeIconPurple from 'src/assets/close_icon_purple.png'
import filledLightGreenCheckbox from 'src/assets/filled_checkbox_light_green.png'
import { APP_CONSTANTS, defaultBgImgId } from 'src/consumer/constants'
import { ProfileContext } from 'src/consumer/context/ProfileContext'
import { SetupContext } from 'src/consumer/context/SetupContext'
import { getBodyForSavingJournal } from 'src/consumer/helpers/dailyPlayHelper'
import {
  SEOComponent,
  formatNumberWithMinTwoDigits,
  openInNewTab,
} from 'src/consumer/helpers/utilFunctions'
import { mediaURL } from 'src/consumer/services'
import {
  getArticleById,
  getFeatureItemBySlug,
  saveAssessment,
} from 'src/consumer/services/featureServices'
import { getImageLink } from 'src/mentor/helpers/utilityFunctions'
import { GenericDisplayFlexColumn } from 'src/mentor/layout/Styles'
import { isTokenInvalidOrAbsent } from 'src/views/pages/login/ProtectedRoute'
import { v4 as uuidv4 } from 'uuid'
import { PlanCardWithTypeChoice } from '../ActivityPlanPublicListing/PlanCardWithTypeChoice'
import {
  InfoAndSingleActionComponentVarOne,
  InfoWithHeadingDescriptionAndImage,
  RelatedAppFeaturesComponent,
} from '../ListingPageComponentsAndHelpers/ListingPageComponents'
import { LoadingSpinner } from '../Loaders'
import { GenericHeaderBackButton } from '../MyPlansPage/MyPlanComponents'
import { FAQ_LAYOUTS } from '../PrewallAndPaywall/faqComponents/utils'
import FaqSection from '../PrewallAndPaywall/FaqSection'
import PrewallAndPaywall from '../PrewallAndPaywall/PrewallAndPaywall'
import { PublicAppFooter } from '../PublicAppFooter'
import PublicAppHeader from '../PublicAppHeader'
import {
  LandingPageBackgroundShade,
  RelatedAppFeaturesStyledHeading,
} from '../ServiceProvidersListingPage/ServiceProvidersListingComponentsStyles'
import { ClubbedAuthModal } from '../SignIn/ClubbedAuthComponents'
import AssessmentStyledSelectionSlider, { APListingComponent } from './AssessmentPlayerComponents'
import { modifyQusAnsDataArray } from './AssessmentPlayerHelpers'
import {
  ACQComparativeAndSliderContainer,
  ACQuestionItem,
  ACQuestionItemSeparator,
  AssessmentComparativeQuestionContainer,
  AssessmentContinueButton,
  AssessmentInputTextArea,
  AssessmentInputTextContainer,
  AssessmentPlayerBackground,
  AssessmentPlayerChartDescriptionSpan,
  AssessmentPlayerChartTitleSpan,
  AssessmentPlayerExtraLink,
  AssessmentPlayerFooterText,
  AssessmentPlayerHeader,
  AssessmentPlayerInformationPageActionButton,
  AssessmentPlayerInformationPageContainer,
  AssessmentPlayerInformationPageDescription,
  AssessmentPlayerInformationPagePicture,
  AssessmentPlayerInformationPageTitle,
  AssessmentPlayerInteractionPageContainer,
  AssessmentPlayerQuestion,
  AssessmentPlayerQuestionNumber,
  AssessmentPlayerQuestionSubText,
  AssessmentPlayerQuestionSuperText,
  AssessmentPlayerResultButton,
  AssessmentPlayerResultButtonsContainer,
  AssessmentPlayerResultCloseButton,
  AssessmentPlayerResultPageContainer,
  AssessmentPlayerResultPageMainPartContainer,
  AssessmentPlayerSecondHeader,
  AssessmentProgressBarContainer,
  AssessmentProgressBarFilled,
  AssessmentSelectOptionBox,
  AssessmentSelectOptonDot,
  AssessmentSelectOptonMultipleIcon,
  DetailedReportComponent,
  DetailedReportDrawer,
  SpeedometerComponent
} from './AssessmentPlayerStyles'

export default function AssessmentPlayer() {
  //
  //
  const navigate = useNavigate()
  const startingTime = useMemo(() => {
    return dayjs()
  }, [])

  //
  // dynamic part of url related:
  const { slug, assessmentId } = useParams()

  //
  // params related:
  const urlParams = new URLSearchParams(window.location.search) // respected params: ref, refType, src, srcId, requestedPageStep, continueFlow
  const ref = urlParams?.has('ref') ? urlParams.get('ref') : ''
  const refType = urlParams?.has('refType') ? urlParams.get('refType') : ''
  const src = urlParams?.has('src') ? urlParams.get('src') : ''
  const srcId = urlParams?.has('src-id') ? urlParams.get('src-id') : ''
  const requestedPageStep = urlParams?.has('step') ? urlParams.get('step') : ''
  const continueFlow = urlParams?.has('continue-flow') ? urlParams.get('continue-flow') : ''

  //
  // uselocation and passed data related:
  const { state } = useLocation()

  //
  // contexts:
  const { profileData, profileDataLoading } = useContext(ProfileContext)
  const { setupData } = useContext(SetupContext)

  //
  // data states:
  const [assessmentData, setAssessmentData] = useState()
  const [userAnswers, setUserAnswers] = useState()
  const [dataLoading, setDataLoading] = useState(false)
  const [resultsLoading, setResultsLoading] = useState(false)
  const [resultsData, setResultsData] = useState()
  const [detailedReportData, setDetailedReportData] = useState([])
  const [totalQuestions, setTotalQuestions] = useState()

  //
  // functional states:
  const [pageState, setPageState] = useState('')
  const [selectedQuestionIndex, setSelectedQuestionIndex] = useState(0)
  const [showClubbedAuthModal, setShowClubbedAuthModal] = useState(false)
  const [typeOfClubbedAuthModal, setTypeOfClubbedAuthModal] = useState('login')
  const [prewallOpen, setPrewallOpen] = useState(false)
  const [detailedReportDrawerOpen, setDetailedReportDrawerOpen] = useState(false)
  const [detailedReportWantedTab, setDetailedReportWantedTab] = useState(null)
  const [showActivityPlans, setShowActivityPlans] = useState(false)

  //
  // use-effects:
  useEffect(() => {
    if (profileDataLoading === false) {
      pageSetup()
    }
  }, [profileDataLoading])

  useEffect(() => {
    if (pageState === 'end_page') {
      onComingToEndPage()
    }
  }, [pageState])

  //
  // general funcs:
  async function pageSetup() {
    setDataLoading(true)
    let response
    if (state?.passedAssessmentData) {
      response = { status: 200, data: state?.passedAssessmentData }
      window.history.replaceState({}, '')
      // might run into some bugs because of changes 28 mar:
    } else if (slug) {
      response = await getFeatureItemBySlug('assessment', slug, true)
    } else {
      response = await getFeatureItemBySlug('assessment', assessmentId, false)
    }
    if (response?.status === 200 && response?.data && Object.keys(response?.data)?.length !== 0) {
      setAssessmentData(response?.data?.data?.assessment)
      setTotalQuestions(response?.data?.data?.assessment?.journalQusAns?.length)
      // if this comes from session storage, then set it here else set blank data
      if (requestedPageStep === 'result' && continueFlow && sessionStorage.getItem(continueFlow)) {
        setUserAnswers(JSON.parse(sessionStorage.getItem(continueFlow)))
      } else {
        setUserAnswers(response?.data?.data?.assessment?.journalQusAns)
      }
      setDataLoading(false)
    } else {
      navigate('/404')
    }
    if (requestedPageStep === 'result' && continueFlow && sessionStorage.getItem(continueFlow)) {
      console.log(amIAllowedToSeeResults(response?.data?.data?.assessment, profileData))
      if (amIAllowedToSeeResults(response?.data?.data?.assessment, profileData) === 'success') {
        setPageState('end_page')
      } else {
        setPageState('interaction')
        setSelectedQuestionIndex(response?.data?.data?.assessment?.journalQusAns?.length - 1)
      }
    } else if (ref && refType === 'clinic') {
      console.log(amIAllowedToAttempt(response?.data?.data?.assessment, profileData))
      if (amIAllowedToAttempt(response?.data?.data?.assessment, profileData) === 'success') {
        setPageState('start_page')
      } else {
        setPageState('landing_page')
      }
    } else {
      setPageState('landing_page')
    }
  }

  async function onComingToEndPage() {
    setResultsLoading(true)
    let timeOnSubmit = dayjs()
    let duration = timeOnSubmit.diff(startingTime, 'milliseconds')
    const modifiedUserAnswersForSubmission = userAnswers?.map((userAnswerItem) => {
      if (userAnswerItem?.answerType === 'option-select-multiple') {
        const multipleAnswerString = userAnswerItem?.answere?.join(', ')
        userAnswerItem.answere = multipleAnswerString
      }
      return userAnswerItem
    })
    const body = getBodyForSavingJournal(
      assessmentData?.id,
      modifiedUserAnswersForSubmission,
      assessmentData?.journalType,
      startingTime,
      duration,
    )
    const responseSaveAssessment = await saveAssessment(
      assessmentData?.id,
      body,
      src || assessmentData?.callSource,
      srcId || assessmentData?.dfid,
    )
    if (responseSaveAssessment?.status === 200) {
      setResultsData(responseSaveAssessment?.data)
      responseSaveAssessment?.data?.forEach(async (item) => {
        const scoringRangeItem = item?.scoringRangeList?.find(
          (itemInScoringList) =>
            itemInScoringList?.min <= item?.userScore && itemInScoringList?.max >= item?.userScore,
        )
        const responseGetArticle = await getArticleById(scoringRangeItem?.articleId)
        setDetailedReportData((prev) => [
          ...prev,
          { domain: item?.domainId, articleData: responseGetArticle?.data },
        ])
      })
      setResultsLoading(false)
    } else {
      setResultsLoading(false)
      setPageState('interaction')
    }
  }

  const handleBackButtonClick = () => {
    if (pageState === 'start_page') {
      setPageState('landing_page')
    }
    if (pageState === 'interaction') {
      if (selectedQuestionIndex === 0) {
        setPageState('start_page')
      } else {
        setSelectedQuestionIndex((prev) => prev - 1)
      }
    }
  }

  const handleNextButtonClick = async () => {
    if (selectedQuestionIndex === totalQuestions - 1) {
      await handleLastQuestionAttemptClick()
    } else {
      setSelectedQuestionIndex((prev) => {
        if (prev < totalQuestions - 1) {
          return prev + 1
        } else {
          return prev
        }
      })
    }
  }

  //
  // validation func:
  const checkNextClickDisabled = () => {
    if (
      userAnswers?.[selectedQuestionIndex]?.answerType === 'option-select-single' &&
      !Boolean(userAnswers?.[selectedQuestionIndex]?.answere)
    ) {
      return true
    }
    if (
      userAnswers?.[selectedQuestionIndex]?.answerType === 'option-select-multiple' &&
      !Array.isArray(userAnswers?.[selectedQuestionIndex]?.answere)
    ) {
      return true
    }
    if (
      userAnswers?.[selectedQuestionIndex]?.answerType === 'option-select-multiple' &&
      userAnswers?.[selectedQuestionIndex]?.answere?.length === 0
    ) {
      return true
    }
    if (
      userAnswers?.[selectedQuestionIndex]?.answerType === 'textBox' &&
      !Boolean(userAnswers?.[selectedQuestionIndex]?.answere)
    ) {
      return true
    }
    return false
  }

  //
  //
  // two main funcs:
  const handleStartLandingPage = () => {
    const accessCheck = amIAllowedToAttempt(assessmentData, profileData) // loginStep, premiumBuyStep, success
    console.log('allowed to attempt = ', accessCheck)
    if (accessCheck === 'loginStep') {
      setShowClubbedAuthModal(true)
    } else if (accessCheck === 'premiumBuyStep') {
      setPrewallOpen(true)
    } else {
      setPageState('start_page')
    }
  }

  const handleLastQuestionAttemptClick = async () => {
    console.log('allowed to see results = ', amIAllowedToSeeResults(assessmentData, profileData))
    const userDataId = uuidv4()
    sessionStorage.setItem(userDataId, JSON.stringify(userAnswers))
    if (amIAllowedToSeeResults(assessmentData, profileData) === 'loginStep') {
      setShowClubbedAuthModal(true)
      navigate(`${window.location.pathname}?step=result&continue-flow=${userDataId}`)
    }
    if (amIAllowedToSeeResults(assessmentData, profileData) === 'premiumBuyStep') {
      setPrewallOpen(true)
      navigate(`${window.location.pathname}?step=result&continue-flow=${userDataId}`)
    }
    if (amIAllowedToSeeResults(assessmentData, profileData) === 'success') {
      navigate(`${window.location.pathname}?step=result&continue-flow=${userDataId}`)
      setPageState('end_page')
    }
  }

  return (
    <>
      {assessmentData && (
        <SEOComponent
          title={`Take a ${assessmentData?.title} Test | AUMHUM`}
          description={
            assessmentData?.description ||
            `Take AUMHUM's free, clinically-vetted ${assessmentData?.title} test to see if you have the signs and symptoms.`
          }
          image={assessmentData && getImageLink(assessmentData?.coverIcon)}
        />
      )}
      {dataLoading ? (
        <LoadingSpinner bgColor="#F5F5F599" dark />
      ) : (
        <>
          {/* boilerplate stuff */}
          <PublicAppHeader
            config={{ showLinks: false }}
            loginFunction={() => {
              setTypeOfClubbedAuthModal('login')
              setShowClubbedAuthModal(true)
            }}
            registerFunction={() => {
              setTypeOfClubbedAuthModal('signup')
              setShowClubbedAuthModal(true)
            }}
            propUserProfileData={profileData}
          />
          <ClubbedAuthModal
            open={showClubbedAuthModal}
            onClose={() => setShowClubbedAuthModal(false)}
            loginVariant={typeOfClubbedAuthModal === 'login'}
          />
          <PrewallAndPaywall
            id="journal_play"
            secureData={setupData}
            wallIsOpen={prewallOpen}
            toggleWall={() => {
              setPrewallOpen((prev) => !prev)
            }}
            onSuccessfulPaymentFunction={() => window.location.reload()}
          />

          {/* actual stuff */}
          {pageState !== 'result_page' && (
            <AssessmentPlayerBackground
              noPadding={pageState === 'landing_page'}
              noBottomPadding={
                assessmentData?.journalQusAns?.[selectedQuestionIndex]?.answerType === 'textBox' ||
                pageState === 'landing_page'
              }
            >
              {(pageState === 'interaction' || pageState === 'start_page') && (
                <AssessmentPlayerHeader>
                  {(pageState === 'interaction' || pageState === 'start_page') && (
                    <GenericHeaderBackButton
                      clickfunction={handleBackButtonClick}
                      styles={{ position: 'absolute', left: '20px', cursor: 'pointer' }}
                    />
                  )}
                  {pageState === 'interaction' && (
                    <AssessmentProgressBarContainer>
                      <AssessmentProgressBarFilled
                        progress={((selectedQuestionIndex + 1) / totalQuestions) * 100}
                      />
                    </AssessmentProgressBarContainer>
                  )}
                </AssessmentPlayerHeader>
              )}

              {pageState === 'interaction' && (
                <AssessmentPlayerSecondHeader>
                  <AssessmentPlayerQuestionSuperText>
                    {(assessmentData?.journalQusAns?.[selectedQuestionIndex]?.superText
                      ? String(assessmentData.journalQusAns[selectedQuestionIndex].superText).toUpperCase()
                      : 'QUESTION')}
                  </AssessmentPlayerQuestionSuperText>
                  <div>
                    <AssessmentPlayerQuestionNumber main>
                      {formatNumberWithMinTwoDigits(selectedQuestionIndex + 1)}
                    </AssessmentPlayerQuestionNumber>
                    <AssessmentPlayerQuestionNumber>
                      /{totalQuestions}
                    </AssessmentPlayerQuestionNumber>
                  </div>
                </AssessmentPlayerSecondHeader>
              )}

              {pageState === 'landing_page' && (
                <>
                  <InfoAndSingleActionComponentVarOne
                    headline={assessmentData?.title}
                    description={assessmentData?.description}
                    image={getImageLink(assessmentData?.coverIcon, defaultBgImgId)}
                    actionButtonText="Begin Test"
                    clickFunction={() => handleStartLandingPage()}
                  />
                  <LandingPageBackgroundShade>
                    <InfoWithHeadingDescriptionAndImage
                      headline="You are not alone"
                      description="Life can be challenging, and there are moments when it feels like the weight of the world is on your shoulders. But you don't have to face your struggles all by yourself. We are ready to provide the help and guidance you need to navigate through difficult times."
                      image={getImageLink(assessmentData?.coverIcon, defaultBgImgId)}
                    />
                  </LandingPageBackgroundShade>
                  <LandingPageBackgroundShade main>
                    <RelatedAppFeaturesStyledHeading>
                      What mental health support do you need?
                    </RelatedAppFeaturesStyledHeading>
                    <PlanCardWithTypeChoice
                      cards={[
                        {
                          type: 'premade',
                          data: {
                            image: '',
                            textOne: dayjs().format('MMMM'),
                            textTwo: '7 - DAY',
                            textThree: 'Anxiety Challenge',
                            linkPathname: '/plan/deal-with-anxiety',
                          },
                        },
                        {
                          type: 'therapist',
                          data: {
                            image: '',
                            textOne: '',
                            textTwo: '',
                            textThree: 'Talk to an expert Therapist',
                            linkPathname: '/therapy-psychiatry',
                          },
                        },
                        {
                          type: 'psychiatry',
                          data: {
                            image: '',
                            textOne: 'Medicine',
                            textTwo: ' & ',
                            textThree: 'Psychiatry',
                            linkPathname: '/therapy-psychiatry',
                          },
                        },
                      ]}
                    />
                  </LandingPageBackgroundShade>
                  <LandingPageBackgroundShade>
                    <RelatedAppFeaturesComponent
                      domainsArray={assessmentData?.domainList}
                      featuresArrayInOrder={['mentor', 'course']}
                      courseCategory={'therepy'}
                    />
                    <RelatedAppFeaturesComponent
                      domainsArray={assessmentData?.domainList}
                      featuresArrayInOrder={['course', 'article']}
                      courseCategory={'meditation'}
                    />
                  </LandingPageBackgroundShade>
                  <LandingPageBackgroundShade main>
                    <FaqSection
                      categoryIds={assessmentData?.faqCategories}
                      layoutType={FAQ_LAYOUTS.TWO_COLUMN_SIDEBAR}
                    />
                  </LandingPageBackgroundShade>
                  <PublicAppFooter />
                </>
              )}

              {pageState === 'start_page' && (
                <>
                  <AssessmentPlayerInformationPageContainer>
                    <AssessmentPlayerInformationPagePicture
                      src={
                        assessmentData?.startScreen?.imageId
                          ? `${mediaURL}${assessmentData?.startScreen?.imageId}`
                          : `${mediaURL}${defaultBgImgId}`
                      }
                    />
                    <AssessmentPlayerInformationPageTitle>
                      {assessmentData?.startScreen?.title}
                    </AssessmentPlayerInformationPageTitle>
                    <AssessmentPlayerInformationPageDescription>
                      {assessmentData?.startScreen?.subText}
                    </AssessmentPlayerInformationPageDescription>
                    <AssessmentPlayerInformationPageActionButton
                      onClick={() => setPageState('interaction')}
                    >
                      {assessmentData?.startScreen?.actionBtnText || 'Start test'}
                    </AssessmentPlayerInformationPageActionButton>
                    {assessmentData?.startScreen?.extraLink &&
                      assessmentData?.startScreen?.extraLinkUri &&
                      assessmentData?.startScreen?.extraLinkText && (
                        <AssessmentPlayerExtraLink>or</AssessmentPlayerExtraLink>
                      )}
                    {assessmentData?.startScreen?.extraLink &&
                      assessmentData?.startScreen?.extraLinkUri &&
                      assessmentData?.startScreen?.extraLinkText && (
                        <AssessmentPlayerExtraLink
                          link={true}
                          onClick={() => openInNewTab(assessmentData?.startScreen?.extraLinkUri)}
                        >
                          {assessmentData?.startScreen?.extraLinkText}
                        </AssessmentPlayerExtraLink>
                      )}
                  </AssessmentPlayerInformationPageContainer>
                  <AssessmentPlayerFooterText>
                    If you are in a life threatening situation, don&apos;t use this site. Please get
                    immediate help.
                  </AssessmentPlayerFooterText>
                </>
              )}

              {pageState === 'end_page' && (
                <AssessmentPlayerInformationPageContainer>
                  <AssessmentPlayerInformationPagePicture
                    src={
                      assessmentData?.resultScreen?.imageId
                        ? `${mediaURL}${assessmentData?.resultScreen?.imageId}`
                        : `${mediaURL}${defaultBgImgId}`
                    }
                  />
                  <AssessmentPlayerInformationPageTitle>
                    {assessmentData?.resultScreen?.title}
                  </AssessmentPlayerInformationPageTitle>
                  <AssessmentPlayerInformationPageDescription>
                    {assessmentData?.resultScreen?.subText}
                  </AssessmentPlayerInformationPageDescription>
                  <AssessmentPlayerInformationPageActionButton
                    disabled={resultsLoading}
                    onClick={() => setPageState('result_page')}
                  >
                    {resultsLoading ? (
                      <>
                        <div>Fetching your results</div>
                        <Spinner animation="border" variant="light" size="sm" />
                      </>
                    ) : (
                      'See your Results'
                    )}
                  </AssessmentPlayerInformationPageActionButton>
                  {assessmentData?.resultScreen?.extraLink &&
                    assessmentData?.resultScreen?.extraLinkUri &&
                    assessmentData?.resultScreen?.extraLinkText && (
                      <AssessmentPlayerExtraLink>or</AssessmentPlayerExtraLink>
                    )}
                  {assessmentData?.resultScreen?.extraLink &&
                    assessmentData?.resultScreen?.extraLinkUri &&
                    assessmentData?.resultScreen?.extraLinkText && (
                      <AssessmentPlayerExtraLink
                        link={true}
                        onClick={() => openInNewTab(assessmentData?.resultScreen?.extraLinkUri)}
                      >
                        {assessmentData?.resultScreen?.extraLinkText}
                      </AssessmentPlayerExtraLink>
                    )}
                </AssessmentPlayerInformationPageContainer>
              )}

              {pageState === 'interaction' && (
                <AssessmentPlayerInteractionPageContainer
                  noBottomPadding={
                    assessmentData?.journalQusAns?.[selectedQuestionIndex]?.answerType === 'textBox'
                  }
                >
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'center', width: '90%', maxWidth: '70%' }}>
                      <AssessmentPlayerQuestion
                        textLeftNoMinHeight={
                          assessmentData?.journalQusAns?.[selectedQuestionIndex]?.answerType ===
                            'option-select-single' &&
                          assessmentData?.journalQusAns?.[selectedQuestionIndex]?.questionType ===
                            'comparative'
                        }
                      >
                        {assessmentData?.journalQusAns?.[selectedQuestionIndex]?.question}
                      </AssessmentPlayerQuestion>
                      {(assessmentData?.journalQusAns?.[selectedQuestionIndex]?.helpText ||
                        assessmentData?.journalQusAns?.[selectedQuestionIndex]?.helptext) && (
                        <Tooltip
                          title={
                            assessmentData.journalQusAns[selectedQuestionIndex].helpText ||
                            assessmentData.journalQusAns[selectedQuestionIndex].helptext
                          }
                          arrow
                          placement="top"
                        >
                          <IconButton size="small" sx={{ p: 0.25, mt: 1 }} aria-label="Help">
                            <InfoOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </div>
                    {(assessmentData?.journalQusAns?.[selectedQuestionIndex]?.subText ||
                      assessmentData?.journalQusAns?.[selectedQuestionIndex]?.subtext) && (
                      <AssessmentPlayerQuestionSubText>
                        {assessmentData.journalQusAns[selectedQuestionIndex].subText ||
                          assessmentData.journalQusAns[selectedQuestionIndex].subtext}
                      </AssessmentPlayerQuestionSubText>
                    )}
                  </div>

                  {/* for comparative questions */}
                  {assessmentData?.journalQusAns?.[selectedQuestionIndex]?.questionType ===
                    'comparative' &&
                    assessmentData?.journalQusAns?.[selectedQuestionIndex]?.answerType ===
                      'option-select-single' && (
                      <ACQComparativeAndSliderContainer>
                        <AssessmentComparativeQuestionContainer>
                          <ACQuestionItem>
                            {assessmentData?.journalQusAns?.[selectedQuestionIndex]?.comparative1}
                          </ACQuestionItem>
                          <GenericDisplayFlexColumn aic style={{ gap: '6px' }}>
                            <ACQuestionItemSeparator />
                            <div>Or</div>
                            <ACQuestionItemSeparator />
                          </GenericDisplayFlexColumn>
                          <ACQuestionItem>
                            {assessmentData?.journalQusAns?.[selectedQuestionIndex]?.comparative2}
                          </ACQuestionItem>
                        </AssessmentComparativeQuestionContainer>

                        <AssessmentStyledSelectionSlider
                          optionsData={
                            modifyQusAnsDataArray(assessmentData?.journalQusAns)?.[
                              selectedQuestionIndex
                            ]?.options
                          }
                          chosenAnswerIds={userAnswers?.[selectedQuestionIndex]?.answerIds}
                          chosenComparative={
                            userAnswers?.[selectedQuestionIndex]?.comparativeSelected
                          }
                          updateFunction={(answere, comparativeSelected, answerIds) =>
                            setUserAnswers((prev) => {
                              const temp = [...prev]
                              temp[selectedQuestionIndex].answere = answere
                              temp[selectedQuestionIndex].comparativeSelected = comparativeSelected
                              temp[selectedQuestionIndex].answerIds = answerIds
                              return temp
                            })
                          }
                        />
                      </ACQComparativeAndSliderContainer>
                    )}

                  {/* for regular single select questions */}
                  {assessmentData?.journalQusAns?.[selectedQuestionIndex]?.questionType ===
                    'regular' &&
                    assessmentData?.journalQusAns?.[selectedQuestionIndex]?.answerType ===
                      'option-select-single' &&
                    assessmentData?.journalQusAns?.[selectedQuestionIndex]?.options?.map(
                      (optionData, optionIndex) => {
                        return (
                          <AssessmentSelectOptionBox
                            key={optionIndex}
                            onClick={() => {
                              setUserAnswers((prev) => {
                                const temp = [...prev]
                                temp[selectedQuestionIndex].answere = optionData?.name
                                temp[selectedQuestionIndex].answerIds = [optionData?.id]
                                return temp
                              })
                              // handleNextButtonClick();
                              setTimeout(() => {
                                handleNextButtonClick()
                              }, 500)
                            }}
                          >
                            {optionData?.name}
                            <AssessmentSelectOptonDot
                              selected={userAnswers?.[selectedQuestionIndex]?.answerIds?.includes(
                                optionData?.id,
                              )}
                            />
                          </AssessmentSelectOptionBox>
                        )
                      },
                    )}

                  {/* for regular multi select questions */}
                  {assessmentData?.journalQusAns?.[selectedQuestionIndex]?.questionType ===
                    'regular' &&
                    assessmentData?.journalQusAns?.[selectedQuestionIndex]?.answerType ===
                      'option-select-multiple' &&
                    assessmentData?.journalQusAns?.[selectedQuestionIndex]?.options?.map(
                      (optionData, optionIndex) => (
                        <AssessmentSelectOptionBox
                          key={optionIndex}
                          onClick={() =>
                            setUserAnswers((prev) => {
                              const temp = [...prev]

                              // for answere:
                              if (Array.isArray(temp[selectedQuestionIndex].answere)) {
                                let itemIndex = temp[selectedQuestionIndex].answere?.indexOf(
                                  optionData?.name,
                                )
                                if (itemIndex === -1) {
                                  temp[selectedQuestionIndex].answere.push(optionData?.name)
                                } else {
                                  temp[selectedQuestionIndex].answere?.splice(itemIndex, 1)
                                }
                              } else {
                                temp[selectedQuestionIndex].answere = [optionData?.name]
                              }

                              // for answer ids array:
                              if (Array.isArray(temp[selectedQuestionIndex].answerIds)) {
                                let itemIndex = temp[selectedQuestionIndex].answerIds?.indexOf(
                                  optionData?.id,
                                )
                                if (itemIndex === -1) {
                                  temp[selectedQuestionIndex].answerIds.push(optionData?.id)
                                } else {
                                  temp[selectedQuestionIndex].answerIds?.splice(itemIndex, 1)
                                }
                              } else {
                                temp[selectedQuestionIndex].answerIds = [optionData?.id]
                              }
                              return temp
                            })
                          }
                        >
                          {optionData?.name}
                          {!userAnswers?.[selectedQuestionIndex]?.answerIds?.includes(
                            optionData?.id,
                          ) && (
                            <AssessmentSelectOptonDot
                              square
                              selected={userAnswers?.[selectedQuestionIndex]?.answerIds?.includes(
                                optionData?.id,
                              )}
                            />
                          )}
                          {userAnswers?.[selectedQuestionIndex]?.answerIds?.includes(
                            optionData?.id,
                          ) && (
                            <AssessmentSelectOptonMultipleIcon
                              src={filledLightGreenCheckbox}
                              alt="filled_checkbox"
                            />
                          )}
                        </AssessmentSelectOptionBox>
                      ),
                    )}

                  {/* for regular textbox questions */}
                  {assessmentData?.journalQusAns?.[selectedQuestionIndex]?.questionType ===
                    'regular' &&
                    assessmentData?.journalQusAns?.[selectedQuestionIndex]?.answerType ===
                      'textBox' && (
                      <AssessmentInputTextContainer>
                        <AssessmentInputTextArea
                          autoFocus
                          placeholder="Enter your answer here"
                          value={userAnswers?.[selectedQuestionIndex]?.answere}
                          onChange={(e) => {
                            setUserAnswers((prev) => {
                              const temp = [...prev]
                              temp[selectedQuestionIndex].answere = e.target.value
                              temp[selectedQuestionIndex].answerIds = []
                              return temp
                            })
                          }}
                        />
                        <AssessmentContinueButton
                          disabled={checkNextClickDisabled()}
                          onClick={handleNextButtonClick}
                        >
                          Continue
                          <ArrowForwardIcon sx={{ fontSize: 20, color: 'white', marginLeft: 1 }} />
                        </AssessmentContinueButton>
                      </AssessmentInputTextContainer>
                    )}

                  {/* for showing continue button */}
                  {((assessmentData?.journalQusAns?.[selectedQuestionIndex]?.questionType ===
                    'regular' &&
                    assessmentData?.journalQusAns?.[selectedQuestionIndex]?.answerType ===
                      'option-select-multiple') ||
                    (assessmentData?.journalQusAns?.[selectedQuestionIndex]?.questionType ===
                      'comparative' &&
                      assessmentData?.journalQusAns?.[selectedQuestionIndex]?.answerType ===
                        'option-select-single')) && (
                    <AssessmentContinueButton
                      disabled={checkNextClickDisabled()}
                      onClick={handleNextButtonClick}
                    >
                      Continue
                      <ArrowForwardIcon sx={{ fontSize: 20, color: 'white', marginLeft: 1 }} />
                    </AssessmentContinueButton>
                  )}
                </AssessmentPlayerInteractionPageContainer>
              )}
            </AssessmentPlayerBackground>
          )}
          {pageState === 'result_page' && (
            <AssessmentPlayerResultPageContainer>
              <AssessmentPlayerResultCloseButton onClick={() => navigate('/')} />

              <AssessmentPlayerResultPageMainPartContainer>
                {resultsData?.map((resultItem, indexResultItem) => {
                  const scoringRangeItem = resultItem?.scoringRangeList?.find(
                    (item) =>
                      item?.min <= resultItem?.userScore && item?.max >= resultItem?.userScore,
                  )

                  const detailedReportForThisDomain = detailedReportData?.find(
                    (detailedReportDataItem) =>
                      detailedReportDataItem?.domain === resultItem?.domainId,
                  )

                  return (
                    <GenericDisplayFlexColumn key={indexResultItem} aic>
                      <SpeedometerComponent
                        resultItem={resultItem}
                        indexResultItem={indexResultItem}
                        scoringRangeItem={scoringRangeItem}
                      />

                      <div style={{ textAlign: 'center' }}>
                        <AssessmentPlayerChartTitleSpan>{`Your ${resultItem?.domainName} score is `}</AssessmentPlayerChartTitleSpan>
                        <AssessmentPlayerChartTitleSpan>{`${resultItem?.userScore}/${resultItem?.totalScore}`}</AssessmentPlayerChartTitleSpan>
                      </div>
                      <AssessmentPlayerChartDescriptionSpan>
                        {scoringRangeItem?.interpretation}
                      </AssessmentPlayerChartDescriptionSpan>
                      {detailedReportForThisDomain?.articleData && (
                        <AssessmentPlayerChartDescriptionSpan
                          style={{ cursor: 'pointer', textDecoration: 'underline' }}
                          detailedReportText
                          onClick={() => {
                            setDetailedReportDrawerOpen(true)
                            setDetailedReportWantedTab(resultItem?.domainId)
                          }}
                        >
                          Tap to view Detailed Report
                        </AssessmentPlayerChartDescriptionSpan>
                      )}
                    </GenericDisplayFlexColumn>
                  )
                })}
              </AssessmentPlayerResultPageMainPartContainer>

              <AssessmentPlayerResultButtonsContainer>
                <AssessmentPlayerResultButton
                  preferred
                  onClick={() => navigate(APP_CONSTANTS.THERAPISTS_PSYCHIATRISTS_LISTING_PAGE_URL)}
                >
                  Get a Therapist
                </AssessmentPlayerResultButton>

                {/* 
                <AssessmentPlayerResultButton
                  onClick={() => {
                    setShowActivityPlans(true)
                  }}
                >
                  Get a Personal Curated Plan
                </AssessmentPlayerResultButton>
                */}

                {/* {(assessmentData?.resultScreen?.actionBtnUri && assessmentData?.resultScreen?.actionBtn && assessmentData?.resultScreen?.actionBtnText) &&
                            <AssessmentPlayerResultButton onClick={() => window.location.href = assessmentData?.resultScreen?.actionBtnUri}>
                                {assessmentData?.resultScreen?.actionBtnText}
                            </AssessmentPlayerResultButton>
                        } */}
              </AssessmentPlayerResultButtonsContainer>

              <DetailedReportDrawer
                className="drawer"
                anchor="bottom"
                open={detailedReportDrawerOpen}
                transitionDuration={{ enter: 600, exit: 300 }}
              >
                <img
                  alt="close_button"
                  src={closeIconPurple}
                  style={{
                    cursor: 'pointer',
                    height: '20px',
                    position: 'absolute',
                    top: '30px',
                    right: '30px',
                  }}
                  onClick={() => {
                    setDetailedReportDrawerOpen(false)
                    setDetailedReportWantedTab(null)
                  }}
                />
                <DetailedReportComponent
                  title="Your Report"
                  detailedReportData={detailedReportData}
                  resultsData={resultsData}
                  tabId={detailedReportWantedTab}
                />
              </DetailedReportDrawer>

              <DetailedReportDrawer
                className="drawer"
                anchor="bottom"
                open={showActivityPlans}
                transitionDuration={{ enter: 600, exit: 300 }}
              >
                <img
                  alt="close_button"
                  src={closeIconPurple}
                  style={{
                    cursor: 'pointer',
                    height: '20px',
                    position: 'absolute',
                    top: '30px',
                    right: '30px',
                  }}
                  onClick={() => {
                    setShowActivityPlans(false)
                  }}
                />
                <APListingComponent
                  domainIds={assessmentData?.domainIds}
                  handleClose={() => {
                    setShowActivityPlans(false)
                  }}
                />
              </DetailedReportDrawer>
            </AssessmentPlayerResultPageContainer>
          )}
        </>
      )}
    </>
  )
}

//
//
// at start page: if user is attempting to start the assessment, first check : if assessment access lock is not result, user needs to be logged in. NEXT: check if assessment paywall-at is not equal to result, then check if user paid, if not show prewall
function amIAllowedToAttempt(assessmentData, profileData) {
  console.log(assessmentData?.accessLock)
  const token = localStorage.getItem('token')
  if (assessmentData?.accessLock === 'result') {
    return 'success'
  } else {
    if (isTokenInvalidOrAbsent(token)) {
      return 'loginStep'
    } else {
      if (
        assessmentData?.paywallAt !== 'result' &&
        assessmentData?.subscription?.id !== 'subscription_free' &&
        profileData?.subscriptionTypeId === 'subscription_free'
      ) {
        return 'premiumBuyStep'
      } else {
        return 'success'
      }
    }
  }
}

//
//
// at results page(end page): if user comes here, we do not need to check the access lock. coming here is proof itself that it can be accessed. 1. we need to make sure he is logged in -- 2. we need to make sure that if the assessment is paid, the user has rights to access it
function amIAllowedToSeeResults(assessmentData, profileData) {
  const token = localStorage.getItem('token')
  if (isTokenInvalidOrAbsent(token)) {
    return 'loginStep'
  } else if (
    assessmentData?.subscription?.id !== 'subscription_free' &&
    profileData?.subscriptionTypeId === 'subscription_free'
  ) {
    return 'premiumBuyStep'
  } else {
    return 'success'
  }
}

//
//
//
//
//
// legacy: not maintaining

// const reduxState = useSelector((state) => state)
// const reduxDispatch = useDispatch()

// {reduxState?.assessmentGoBackState && (
//   <GoBackToPageWhereYouCameFromButton
//     onClick={reduxState?.assessmentGoBackState?.onClick}
//   >
//     {reduxState?.assessmentGoBackState?.text}
//   </GoBackToPageWhereYouCameFromButton>
// )}

// useEffect(() => {
//   pageSetup()
//   return () => {
//     reduxDispatch({ type: 'set', assessmentGoBackState: null })
//   }
// }, [])
